/**
 * Feedback about the assistant itself — the "Send feedback" button in the
 * header. Validation, the request body and the POST to the feedback service
 * (product-ema-feedback, `POST {feedbackApiUrl}/v1/feedback`). Pure apart from
 * `submitFeedback`, so scripts/test-config.mjs can pin the rules against the
 * service's own (`layers/common/common/models.py` › SubmitFeedbackRequest).
 *
 * WHO READS IT. Not the business whose website this is: the service files an
 * anonymous submission for the Ema Answers team, and the form says so before
 * anyone types (`feedbackIntro`). Lead capture is the visitor's way to the
 * business (POST /chat/lead); this is their way to us. Keep the two apart.
 *
 * WHAT IT NEVER CARRIES. The conversation's `sessionId`: it is the capability
 * token that reads the transcript back (GET /chat/history), and a feedback item
 * is stored and e-mailed. Nor the page's query string or fragment, which is
 * where sites put campaign ids and tokens.
 */
import { LEAD_EMAIL_RE } from './lead';
import { version } from './version';

/** The service's `VALID_FEEDBACK_TYPES`, in the order the chips are drawn. */
export const FEEDBACK_TYPES = ['general', 'feature', 'bug', 'love'] as const;
export type FeedbackType = (typeof FEEDBACK_TYPES)[number];

/** Mirrors SubmitFeedbackRequest: message 1–10,000 characters, email ≤ 254. */
export const FEEDBACK_LIMITS = { message: 10_000, email: 254 } as const;

/**
 * The privacy notice of PuglieseWeb LTD, the company behind Ema Answers and the
 * controller of what this form collects — linked from the form, beside the
 * sentence saying who reads it, because a visitor on someone else's website is
 * owed that at the point of collection. NOT the business's `privacyUrl`: that
 * notice covers the conversation the business receives, not this.
 */
export const FEEDBACK_PRIVACY_URL = 'https://www.puglieseweb.com/privacy';

export interface FeedbackInput {
  type: FeedbackType;
  message: string;
  /** Optional: '' when the visitor wants no reply. */
  email: string;
  /** Honeypot, as on the lead form: always '' from a person. */
  website: string;
}

export type FeedbackValidation =
  | { ok: true; value: FeedbackInput }
  | { ok: false; field: 'type' | 'message' | 'email'; reason: 'required' | 'invalid' | 'tooLong' };

export function validateFeedback(raw: FeedbackInput): FeedbackValidation {
  const message = (raw.message ?? '').trim();
  const email = (raw.email ?? '').trim();
  if (!FEEDBACK_TYPES.includes(raw.type)) return { ok: false, field: 'type', reason: 'invalid' };
  if (!message) return { ok: false, field: 'message', reason: 'required' };
  if (message.length > FEEDBACK_LIMITS.message) return { ok: false, field: 'message', reason: 'tooLong' };
  // Optional — but the service stores any string, and a mistyped address is a
  // reply that can never arrive, so the shape is checked here.
  if (email && !LEAD_EMAIL_RE.test(email)) return { ok: false, field: 'email', reason: 'invalid' };
  if (email.length > FEEDBACK_LIMITS.email) return { ok: false, field: 'email', reason: 'tooLong' };
  return { ok: true, value: { type: raw.type, message, email, website: raw.website ?? '' } };
}

/** The page as a feedback item records it: origin and path, never the query or the fragment. */
export function feedbackPage(location: { origin: string; pathname: string }): string {
  return `${location.origin}${location.pathname}`;
}

export interface FeedbackContext {
  tenantId: string;
  /** The language the widget is drawn in (the resolved config's `language`). */
  language: string;
  /** `feedbackPage(window.location)`. */
  page: string;
}

export interface FeedbackRequest {
  type: FeedbackType;
  message: string;
  email?: string;
  metadata: {
    source: 'chat-widget';
    product: 'ema-assistant';
    tenantId: string;
    language: string;
    page: string;
    widgetVersion: string;
  };
}

export function feedbackRequest(input: FeedbackInput, ctx: FeedbackContext): FeedbackRequest {
  return {
    type: input.type,
    message: input.message,
    ...(input.email ? { email: input.email } : {}),
    // Triage only, never a key: the service files every anonymous submission
    // under one partition. `product` is the one the console's own Send
    // feedback files under (components/UserMenu › FeedbackModal), so the team
    // reads one product; `tenantId` says whose assistant the visitor was using.
    metadata: {
      source: 'chat-widget',
      product: 'ema-assistant',
      tenantId: ctx.tenantId,
      language: ctx.language,
      page: ctx.page,
      widgetVersion: version,
    },
  };
}

export type FeedbackOutcome =
  | { kind: 'ok' }
  /** Anything else: a 4xx, a 429 from the stage throttle, a 5xx, the network, the timeout. The form keeps its text. */
  | { kind: 'failed'; code: string };

export async function submitFeedback(feedbackApiUrl: string, body: FeedbackRequest, timeoutMs = 20_000): Promise<FeedbackOutcome> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${feedbackApiUrl}/v1/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    return res.ok ? { kind: 'ok' } : { kind: 'failed', code: `http_${res.status}` };
  } catch {
    return { kind: 'failed', code: ctrl.signal.aborted ? 'timeout' : 'network' };
  } finally {
    clearTimeout(timer);
  }
}
